import { DocumentActions } from '@/components/DocumentActions'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { SendDocumentButton } from '@/components/SendDocument'
import { useConvertToOrder, useQuotation, useQuotationAction } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  PageHeader,
  StatusChip,
} from '@/components/common'

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)

  const { data: quotation, isLoading, error } = useQuotation(id)
  const submitApproval = useQuotationAction('submit-approval')
  const approve = useQuotationAction('approve')
  const confirmAcceptance = useQuotationAction('confirm-acceptance')
  const convert = useConvertToOrder()

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!quotation) return <EmptyState message="Quotation not found." />

  const version = quotation.currentVersion
  const busy = submitApproval.isPending || approve.isPending || convert.isPending
    || confirmAcceptance.isPending

  return (
    <>
      <PageHeader
        title={quotation.quotationNumber}
        subtitle={`${quotation.customerName}${version ? ` · ${humanise(version.serviceType)} on ${formatDate(version.eventDate)}` : ''}`}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotations')}>
              Back
            </Button>
            {/* Not once it is on an order: the order has its own change
                request for that, and revising the quotation underneath it
                would rewrite the terms the customer actually accepted. */}
            {canWrite('quotation') && !quotation.convertedOrderId && (
              <Button
                startIcon={<EditOutlinedIcon />}
                onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
              >
                Edit
              </Button>
            )}
            {version && (
              <DocumentActions
                path={`/api/v1/quotations/${quotation.id}/pdf`}
                fileName={`${quotation.quotationNumber}.pdf`}
                label="quotation"
              />
            )}
            {/* Email or WhatsApp, with the file itself, from the same dialog
                every other document uses. */}
            <SendDocumentButton kind="quotation" id={quotation.id} label="Send…" size="medium" />
            {canWrite('quotation') && version?.status === 'DRAFT' && (
              <Button
                variant="outlined"
                onClick={() => submitApproval.mutate({ id: quotation.id })}
                disabled={busy}
              >
                Submit for approval
              </Button>
            )}
            {can('quotation.approve') && version?.status === 'INTERNAL_APPROVAL' && (
              <Button
                variant="contained"
                onClick={() => approve.mutate({ id: quotation.id, body: { approved: true } })}
                disabled={busy}
              >
                Approve
              </Button>
            )}
            {canWrite('quotation') && version?.status === 'ACCEPTED_AWAITING_CONFIRMATION' && (
              <Button
                variant="contained"
                onClick={() => confirmAcceptance.mutate({ id: quotation.id, body: {} })}
                disabled={busy}
              >
                Confirm acceptance
              </Button>
            )}
            {can('order.confirm') &&
              version?.status === 'CUSTOMER_ACCEPTED' &&
              !quotation.convertedOrderId && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={busy}
                  onClick={async () => {
                    const order = await convert.mutateAsync({ quotationId: quotation.id })
                    navigate(`/orders/${order.id}`)
                  }}
                >
                  Convert to order
                </Button>
              )}
          </Stack>
        }
      />

      <ErrorBanner error={submitApproval.error ?? approve.error ?? convert.error ?? confirmAcceptance.error} />
      {version?.status === 'ACCEPTED_AWAITING_CONFIRMATION' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The customer has accepted this quotation. Check it, then confirm acceptance before it is converted to an order.
        </Alert>
      )}
      {busy && <LinearProgress sx={{ mb: 2 }} />}

      {quotation.acceptanceLink && version?.status === 'SENT' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => navigator.clipboard.writeText(quotation.acceptanceLink!)}
            >
              Copy link
            </Button>
          }
        >
          The customer can review and accept this quotation at{' '}
          <strong>{quotation.acceptanceLink}</strong>
        </Alert>
      )}

      {quotation.convertedOrderId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Converted to an order.{' '}
          <Button size="small" onClick={() => navigate(`/orders/${quotation.convertedOrderId}`)}>
            Open the order
          </Button>
        </Alert>
      )}

      {version && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Version {version.versionNumber}</Typography>
                  <StatusChip status={version.status} size="medium" />
                </Stack>
                <Grid container spacing={2}>
                  <Field label="Event type" value={humanise(version.eventType)} />
                  <Field label="Event date" value={formatDate(version.eventDate)} />
                  <Field
                    label="Guests"
                    value={`${formatNumber(version.guestCount)} (${version.childGuestCount} children)`}
                  />
                  <Field label="Valid until" value={formatDate(version.validUntil)} />
                  <Field label="Sent at" value={formatDateTime(version.sentAt)} />
                  <Field
                    label="Customer decision"
                    value={
                      version.customerResponseAt
                        ? `${version.customerResponseName ?? 'Customer'} · ${formatDateTime(version.customerResponseAt)}`
                        : 'Awaiting'
                    }
                  />
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Charges
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {version.lines.map((line) => (
                      <TableRow key={line.id} hover>
                        <TableCell>
                          <Typography variant="body2">{line.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {humanise(line.lineType)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(line.quantity)} {humanise(line.unit)}
                        </TableCell>
                        <TableCell align="right">{formatMoney(line.unitPrice, true)}</TableCell>
                        <TableCell align="right">{formatMoney(line.lineTotal, true)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider sx={{ my: 2 }} />
                <Stack spacing={0.75} sx={{ maxWidth: 360, ml: 'auto' }}>
                  <Total label="Subtotal" value={version.subtotalAmount} />
                  {version.discountAmount > 0 && (
                    <Total
                      label={`Discount (${version.discountPercent.toFixed(2)}%)`}
                      value={-version.discountAmount}
                    />
                  )}
                  <Total label="Taxable value" value={version.taxableAmount} />
                  <Total label="GST" value={version.taxAmount} />
                  {version.roundOff !== 0 && <Total label="Round off" value={version.roundOff} />}
                  <Divider />
                  <Total label="Total payable" value={version.totalAmount} emphasise />
                  {version.marginAmount !== undefined && version.marginAmount !== null && (
                    <Typography variant="caption" color="text.secondary" align="right">
                      Estimated margin {formatMoney(version.marginAmount)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {version.menuSelections.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected menu
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {version.menuSelections.map((selection) => (
                      <Chip
                        key={selection.id}
                        label={selection.itemName}
                        variant={selection.extra ? 'filled' : 'outlined'}
                        color={selection.extra ? 'secondary' : 'default'}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Approvals
                </Typography>
                {version.approvals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No approval was needed for this discount level.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {version.approvals.map((approval) => (
                      <Stack key={approval.id} spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <StatusChip status={approval.status} />
                          <Typography variant="caption">{humanise(approval.requiredRoleCode)}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {approval.reason}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Terms
                </Typography>
                <Stack spacing={1.5}>
                  {version.terms.map((term) => (
                    <div key={term.id}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {humanise(term.termType)}
                      </Typography>
                      <Typography variant="body2">{term.content}</Typography>
                    </div>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Version history
                </Typography>
                <Table size="small">
                  <TableBody>
                    {quotation.versions.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>v{entry.versionNumber}</TableCell>
                        <TableCell>
                          <StatusChip status={entry.status} />
                        </TableCell>
                        <TableCell align="right">{formatMoney(entry.totalAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  )
}

function Total({
  label,
  value,
  emphasise = false,
}: {
  label: string
  value: number
  emphasise?: boolean
}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant={emphasise ? 'subtitle1' : 'body2'} color="text.secondary">
        {label}
      </Typography>
      <Typography variant={emphasise ? 'subtitle1' : 'body2'} fontWeight={emphasise ? 700 : 500}>
        {formatMoney(value, true)}
      </Typography>
    </Stack>
  )
}
